import { Link } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';

import { Separator } from '../../ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from '../../ui/sidebar';
import { Mark } from '../brand/Mark';
import { IconHint } from '../hint';
import { ShellDrawer } from './ShellDrawer';
import { sh } from './messages';
import { applyLeaf, LeafProvider, Trail, type Crumb, type Leaf } from './trail';

// Below 1024 the aside gives way to `ShellDrawer` entirely -- the registry
// `Sidebar`'s own mobile switch is pinned to 768 and cannot be told otherwise.
const COMPACT_QUERY = '(max-width: 1023px)';
// 1024-1439 opens on the rail and forgets an expansion on reload; only 1440
// and up remembers the viewer's own choice.
const FORCE_RAIL_QUERY = '(max-width: 1439px)';

const COLLAPSE_KEY = 'shell.sidebar.collapsed';

function useShellBand(): { compact: boolean; forceRail: boolean } {
  const [compact, setCompact] = useState(() => window.matchMedia(COMPACT_QUERY).matches);
  const [forceRail, setForceRail] = useState(() => window.matchMedia(FORCE_RAIL_QUERY).matches);
  useEffect(() => {
    const compactQuery = window.matchMedia(COMPACT_QUERY);
    const railQuery = window.matchMedia(FORCE_RAIL_QUERY);
    const onCompact = (): void => setCompact(compactQuery.matches);
    const onRail = (): void => setForceRail(railQuery.matches);
    compactQuery.addEventListener('change', onCompact);
    railQuery.addEventListener('change', onRail);
    return () => {
      compactQuery.removeEventListener('change', onCompact);
      railQuery.removeEventListener('change', onRail);
    };
  }, []);
  return { compact, forceRail };
}

function readCollapsed(defaultOpen: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(COLLAPSE_KEY);
    return stored === null ? !defaultOpen : stored === '1';
  } catch {
    return !defaultOpen;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  } catch {
    // Storage blocked: the choice still applies for this page.
  }
}

export function Shell(props: {
  brand: { label: string; href: string };
  switcher?: ReactNode;
  nav: ReactNode;
  /** The persistent meter above the footer (Figma `122:2`, `247:10768`) --
   *  undefined for a plane with no balance to show (`OpsShell`), never a
   *  placeholder in its place. */
  meter?: ReactNode;
  footer?: ReactNode;
  /** The header (Figma `123:203`, `Kind=Breadcrumb`): the trail from the
   *  outermost context to the screen on show, and the primary action beside
   *  it (Figma `186:6620`). `exact` says the last crumb is the route on
   *  screen rather than an ancestor of it -- see `useBreadcrumbLeaf`. */
  topbar?: {
    crumbs: readonly Crumb[];
    exact: boolean;
    actions?: ReactNode;
  };
  children: ReactNode;
  defaultOpen?: boolean;
}): ReactNode {
  const { compact, forceRail } = useShellBand();
  const [collapsed, setCollapsed] = useState(() => readCollapsed(props.defaultOpen ?? true));
  const [railExpanded, setRailExpanded] = useState(false);
  // Forced expanded in the drawer: the collapsed-icon CSS lives on `Sidebar`
  // itself, which the drawer never renders, but `SidebarMenuButton`'s
  // tooltip reads `state` from context regardless -- collapsed here would
  // show a hover tooltip beside a label the drawer already has room for.
  const open = compact ? true : forceRail ? railExpanded : !collapsed;

  function handleOpenChange(next: boolean): void {
    if (compact) return;
    if (forceRail) {
      setRailExpanded(next);
      return;
    }
    setCollapsed(!next);
    writeCollapsed(!next);
  }

  const [leaf, setLeaf] = useState<Leaf | null>(null);
  const crumbs =
    props.topbar === undefined ? [] : applyLeaf(props.topbar.crumbs, props.topbar.exact, leaf);

  const trigger = (
    <IconHint label={sh('toggle')}>
      <SidebarTrigger />
    </IconHint>
  );

  const footer = (
    <>
      {/* Rail-collapsed (`open` false, drawer excluded -- it is always
       * `true` there) shows the icon-only user row alone: the meter has no
       * room and no icon form of its own, so it drops rather than
       * squeezing. */}
      {props.meter !== undefined && open && props.meter}
      {props.footer}
    </>
  );

  return (
    <SidebarProvider open={open} onOpenChange={handleOpenChange}>
      <a
        href="#conteudo"
        className="bg-background text-foreground sr-only z-50 rounded-md px-3 py-2 focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        {sh('skip')}
      </a>
      {!compact && (
        <Sidebar collapsible="icon">
          <SidebarHeader>
            <SidebarMenu>
              {/* The collapse control lives beside the brand, not the sticky
               * header, so it stays where the sidebar it toggles is; the
               * rail has no width for the pair, so there it stacks above. */}
              <SidebarMenuItem
                data-slot="sidebar-brand-row"
                className={open ? 'flex items-center gap-1' : 'flex flex-col items-center gap-1'}
              >
                {!open && trigger}
                <SidebarMenuButton
                  size="lg"
                  asChild
                  className="flex-1 group-data-[collapsible=icon]:justify-center"
                >
                  <Link to={props.brand.href}>
                    <Mark aria-hidden className="shrink-0" />
                    <span className={open ? 'text-title-sm' : 'sr-only'}>{props.brand.label}</span>
                  </Link>
                </SidebarMenuButton>
                {open && trigger}
              </SidebarMenuItem>
            </SidebarMenu>
            {props.switcher}
          </SidebarHeader>
          <SidebarContent>{props.nav}</SidebarContent>
          <SidebarFooter>{footer}</SidebarFooter>
          <SidebarRail />
        </Sidebar>
      )}
      <SidebarInset>
        <header className="bg-background sticky top-0 z-10 flex h-14 items-center gap-2 border-b px-4">
          {/* Non-compact carries no trigger here: the collapse control lives
           * on the sidebar's own brand row, since the drawer (compact) has
           * no equivalent row of its own to hold one. */}
          {compact && (
            <>
              <ShellDrawer
                brand={props.brand}
                switcher={props.switcher}
                nav={props.nav}
                footer={footer}
              />
              <Separator orientation="vertical" className="h-4" />
            </>
          )}
          {props.topbar === undefined ? (
            // No caller-supplied trail: the persistent aside carries the
            // brand at 1024 and up, so below that this is the only place
            // left to show it -- `lg:hidden` (1024px) rather than the
            // registry's usual `md:hidden` (768px), matching the drawer's
            // own threshold, or 768-1023 loses the brand from both places
            // at once.
            <span className="text-body-sm lg:hidden">{props.brand.label}</span>
          ) : (
            <>
              <div data-slot="topbar-content" className="flex min-w-0 flex-1 items-center">
                <Trail crumbs={crumbs} />
              </div>
              {props.topbar.actions !== undefined && (
                <div className="hidden shrink-0 items-center gap-2xs lg:flex">
                  {props.topbar.actions}
                </div>
              )}
            </>
          )}
        </header>
        {/* The skip link's target: the sticky header above, the compact
         * drawer trigger included, must stay reachable by a normal Tab from
         * the top of the page, so the id sits on the content past it, not on
         * SidebarInset itself. */}
        <div id="conteudo" className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 md:px-8">
          <LeafProvider value={setLeaf}>{props.children}</LeafProvider>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
